import { useEffect, useState } from 'react';
import {
  DEFAULT_QUIET_HOURS,
  HAYAT_WRITE_MODES,
  QUIET_HOUR_PATTERN,
  t,
  type HayatWriteMode,
} from '@aquabox/shared';
import { Link } from 'react-router-dom';
import {
  useAutoAssignGate,
  useMe,
  useSendTestNotification,
  useTenant,
  useTenantMutations,
} from '../api/queries';
import { ApiError } from '../api/client';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import { useToast } from '../components/Toast';
import type { AutoAssignGate } from '../api/types';

/** Eşik alanları: ad, yardım metni ve sunucudaki alt/üst sınır (tenant-settings.schema.ts). */
const THRESHOLDS = [
  {
    key: 'unassignedOrderAlertMinutes' as const,
    labelKey: 'set.unassignedMinutes' as const,
    hintKey: 'set.unassignedMinutesHint' as const,
    unitKey: 'set.unitMinutes' as const,
    min: 5,
    max: 240,
  },
  {
    key: 'kmVarianceThresholdKm' as const,
    labelKey: 'set.kmVariance' as const,
    hintKey: 'set.kmVarianceHint' as const,
    unitKey: 'set.unitKm' as const,
    min: 1,
    max: 200,
  },
  {
    key: 'lowStockThreshold' as const,
    labelKey: 'set.lowStock' as const,
    hintKey: 'set.lowStockHint' as const,
    unitKey: 'set.unitJugs' as const,
    min: 0,
    max: 5000,
  },
];

/** "Uyum %74 · 7/7 günlük veri…" satırı; sayılar sunucudan gelir, burada hesap yok. */
function gateSummary(gate: AutoAssignGate): string {
  const parts: string[] = [
    gate.ratePct === null
      ? t('set.autoAssign.rateNone')
      : t('set.autoAssign.rate', {
          rate: gate.ratePct.toLocaleString('tr-TR', { maximumFractionDigits: 1 }),
        }),
  ];
  if (gate.comparableDays < gate.minDays) {
    parts.push(t('set.autoAssign.daysShort', { days: gate.comparableDays, min: gate.minDays }));
  }
  if (gate.comparable < gate.minSamples) {
    parts.push(t('set.autoAssign.samplesShort', { n: gate.comparable, min: gate.minSamples }));
  }
  if (gate.eligible) parts.push(t('set.autoAssign.ready'));
  return parts.join(' · ');
}

/**
 * Ayarlar (`ayarlar`).
 *
 * Prototipte "kapsam dışı" işaretliydi. Gerçek üründe yer tutucu olamaz, çünkü BU EKRAN
 * KILL SWITCH'İN EVİDİR (CLAUDE.md kural 10): "marka durun derse aynı gün duracak anahtar
 * panelde bulunur, koda gömülmez". Anahtar panelde olmazsa kural yazılı bir niyet olarak kalır.
 *
 * Eşikler burada, uyarı motorunda değil: "25 dakikadır atanmadı" gibi bir sayı koda gömülünce
 * bayinin yoğun gününde değiştirilemez hâle gelir.
 */
export function Settings() {
  const toast = useToast();
  const me = useMe();
  const tenant = useTenant();
  const { updateSettings, updateFlags, updateHayatWriteMode, updateAssignmentMode } =
    useTenantMutations();
  const sendTest = useSendTestNotification();
  const [draft, setDraft] = useState<Record<string, string>>({});
  // Hayat kip ayarı taslak state'i: ekranda iki kutu da aynı değere yazıldığında düğme açılır.
  const [modeDraft, setModeDraft] = useState<HayatWriteMode | null>(null);
  const [confirmDraft, setConfirmDraft] = useState<HayatWriteMode | null>(null);
  const isOwner = me.data?.role === 'OWNER';
  // Sessiz saat taslağı (N1): sunucu değerinden BİR KEZ başlar (eşik taslağıyla aynı desen).
  const [quietDraft, setQuietDraft] = useState<{ start: string; end: string } | null>(null);
  const serverQuiet = tenant.data?.settings.quietHours ?? DEFAULT_QUIET_HOURS;
  const quietValid =
    quietDraft !== null &&
    QUIET_HOUR_PATTERN.test(quietDraft.start) &&
    QUIET_HOUR_PATTERN.test(quietDraft.end);
  const quietDirty =
    quietDraft !== null &&
    (quietDraft.start !== serverQuiet.start || quietDraft.end !== serverQuiet.end);
  const gateQ = useAutoAssignGate(me.data?.role === 'OWNER' || me.data?.role === 'MANAGER');
  const assignmentMode = tenant.data?.settings.orderAssignmentMode ?? 'MANUAL';

  function changeAssignmentMode(mode: 'AUTO' | 'MANUAL') {
    updateAssignmentMode.mutate(mode, {
      onSuccess: () => toast(t('set.saved')),
      onError: (err) =>
        toast(err instanceof ApiError ? err.message : t('error.internal'), 'danger'),
    });
  }

  // Sunucudan gelen değerler forma BİR KEZ yazılır; sonraki tazelemeler kullanıcının yazdığını
  // ezmez (kayıt sırasında gelen bir refetch, yarım yazılmış sayıyı geri almamalı).
  useEffect(() => {
    if (!tenant.data || Object.keys(draft).length > 0) return;
    setDraft(
      Object.fromEntries(THRESHOLDS.map((f) => [f.key, String(tenant.data.settings[f.key] ?? '')])),
    );
  }, [tenant.data, draft]);

  const flags = tenant.data?.flags ?? {};
  const dirty = THRESHOLDS.some(
    (f) =>
      draft[f.key] !== undefined && draft[f.key] !== String(tenant.data?.settings[f.key] ?? ''),
  );

  useEffect(() => {
    if (!tenant.data || quietDraft !== null) return;
    setQuietDraft({ ...(tenant.data.settings.quietHours ?? DEFAULT_QUIET_HOURS) });
  }, [tenant.data, quietDraft]);

  function saveQuietHours(): void {
    if (!isOwner || !quietValid || !quietDirty || quietDraft === null) return;
    updateSettings.mutate(
      { quietHours: { start: quietDraft.start, end: quietDraft.end } },
      {
        onSuccess: () => toast(t('set.quietSaved')),
        onError: (err) =>
          toast(err instanceof ApiError ? err.message : t('error.internal'), 'danger'),
      },
    );
  }

  /**
   * Kendine test bildirimi gönder (`POST /api/notifications/test`).
   *
   * Bildirim hattı çalışıyor mu diye elle denenir. TÜM roller (patron ve yönetici) görür.
   * Hız sınırı sunucudadır: 60 saniye içinde ikinci çağrı 429 döner ve o hatanın mesajı
   * SUNUCUNUN KENDİ metnidir — panelde olduğu gibi gösterilir.
   */
  function sendTestNotification(): void {
    sendTest.mutate(undefined, {
      onSuccess: () => toast(t('panel.settings.testNotification.success')),
      onError: (err) =>
        toast(
          err instanceof ApiError ? err.message : t('panel.settings.testNotification.error'),
          'danger',
        ),
    });
  }

  // Hayat kipi taslaklarını sunucu değerinden BİR KEZ başlat; sonraki tazelemeler kullanıcının
  // yazdığını ezmesin. Settings için THRESHOLDS'la aynı desen.
  useEffect(() => {
    if (!tenant.data || modeDraft !== null) return;
    const initial = tenant.data.settings.hayatWriteMode ?? 'SHADOW';
    setModeDraft(initial);
    setConfirmDraft(initial);
  }, [tenant.data, modeDraft]);

  // Hayat kip değişimi yalnız OWNER. Mevcut kip taslakla aynıysa düğme pasif; iki kutu da aynı
  // değilse (confirmMismatch) düğme yine pasif — sunucu da bunu RED eder, eşleşen ekran kapalıyken
  // "Gönder"e basılırsa göze çarpan bir hata yerine sessizce pasif kalmak daha az yanıltıcı.
  const hayatModeValid =
    isOwner &&
    modeDraft !== null &&
    confirmDraft !== null &&
    modeDraft === confirmDraft &&
    modeDraft !== (tenant.data?.settings.hayatWriteMode ?? 'SHADOW');

  function submitHayatMode(): void {
    if (!hayatModeValid || modeDraft === null) return;
    updateHayatWriteMode.mutate(
      { mode: modeDraft, confirmMode: modeDraft },
      {
        onSuccess: () => toast(t('set.saved')),
        onError: (err) =>
          toast(err instanceof ApiError ? err.message : t('error.internal'), 'danger'),
      },
    );
  }

  function save(): void {
    const body: Record<string, number> = {};
    for (const f of THRESHOLDS) {
      const raw = draft[f.key];
      if (raw === undefined || raw.trim() === '') continue;
      const n = Number(raw);
      if (!Number.isInteger(n) || n < f.min || n > f.max) continue;
      if (n !== tenant.data?.settings[f.key]) body[f.key] = n;
    }
    if (Object.keys(body).length === 0) return;
    updateSettings.mutate(body, { onSuccess: () => toast(t('set.saved')) });
  }

  return (
    <div
      style={{
        padding: '28px 32px 36px',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        maxWidth: 900,
      }}
    >
      {/* ── Kill switch: ekranın en üstünde, çünkü acil durumda aranan şey budur ── */}
      <section
        className="card"
        style={{
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          borderColor: 'var(--warning)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="alert-triangle" size={18} />
          <strong style={{ fontSize: 15 }}>{t('set.killSwitch')}</strong>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
          {t('set.killSwitchHint')}
        </p>
        {updateFlags.isError ? <ErrorLine error={updateFlags.error} /> : null}
        {/* Çekim anahtarları (Hayat/Abant/Getir) yalnız Entegrasyonlar'da yazılır; burada durum
             gösterilir. Kontrol tek yerde kalır — iki yerde aynı bayrağı yazan düğme olmasın. */}
        <FlagStatus label={t('set.flagHayat')} on={flags.scraperHayat === true} />
        <FlagStatus label={t('set.flagAbant')} on={flags.scraperAbant === true} />
        <FlagStatus label={t('set.flagGetir')} on={flags.scraperGetir === true} />
        <p style={{ fontSize: 13, margin: 0 }}>
          <Link to="/entegrasyonlar">{t('set.killSwitchManage')}</Link>
        </p>
        <FlagRow
          label={t('set.flagPos')}
          on={flags.posEnabled === true}
          pending={updateFlags.isPending}
          onToggle={(enabled) =>
            updateFlags.mutate(
              { posEnabled: enabled },
              { onSuccess: () => toast(enabled ? t('set.flagOn') : t('set.flagOff')) },
            )
          }
        />
        {/* ── Hayat yazma yetkisi (kural 10'la aynı ailede acil kapatma). Kapatma HERKES için
             serbest; AÇMA yalnız OWNER — ekratche tüm rollere sunuyorum, "kapat" herkes kullanabilsin
             diye. Asıl kapı (AÇMA) sunucudadır. ── */}
        <FlagRow
          label={t('hayatPush.setting.writerEnabled')}
          on={flags.hayatWriterEnabled === true}
          pending={updateFlags.isPending}
          offBody={t('set.flagOffBodyWriter')}
          onToggle={(enabled) =>
            updateFlags.mutate(
              { hayatWriterEnabled: enabled },
              {
                onSuccess: () => toast(enabled ? t('set.flagOn') : t('set.flagOff')),
                onError: (err) =>
                  toast(err instanceof ApiError ? err.message : t('error.internal'), 'danger'),
              },
            )
          }
        />
      </section>

      <SettingsGroup
        id="business"
        title={t('set.group.business')}
        hint={t('set.group.businessHint')}
      >
        {/* ── Bayi künyesi: salt okunur, tek bayi olduğu için buradan değiştirilmiyor ── */}
        <section
          className="card"
          style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          <strong style={{ fontSize: 15 }}>{t('set.tenant')}</strong>
          <Row
            label={t('set.tenantName')}
            value={tenant.data?.name ?? '—'}
            hint={t('set.tenantNameHint')}
          />
          <Row
            label={t('set.timezone')}
            value={tenant.data?.timezone ?? '—'}
            hint={t('set.timezoneHint')}
          />
          <Row
            label={t('set.accessStatus')}
            value={tenant.data ? t(`tenantStatus.${tenant.data.status}`) : '—'}
            hint={t('set.accessStatusHint')}
          />
          <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
            {t('set.tenantHint')}
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
            {t('set.panelVersion')}: {__APP_VERSION__}
          </p>
        </section>

        {/* ── Yeni siparişlerin atanması (panel §3.6). Yönetici de görebilir/değiştirebilir. ── */}
        <section
          className="card"
          style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ fontSize: 15 }}>{t('set.orderAssignment')}</strong>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
            {t('set.orderAssignmentHint')}
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong data-testid="auto-assign-state" style={{ fontSize: 14 }}>
              {assignmentMode === 'AUTO'
                ? t('set.autoAssign.stateOn')
                : t('set.autoAssign.stateOff')}
            </strong>
            {assignmentMode === 'AUTO' ? (
              // Kapatmak her zaman serbest ve kapısız: sunucu da MANUAL'a geçişte kapı aramaz.
              <button
                className="btn"
                data-testid="auto-assign-close"
                disabled={updateAssignmentMode.isPending}
                onClick={() => changeAssignmentMode('MANUAL')}
              >
                {t('set.autoAssign.close')}
              </button>
            ) : isOwner ? (
              <button
                className="btn btn-primary"
                data-testid="auto-assign-open"
                disabled={updateAssignmentMode.isPending || gateQ.data?.eligible !== true}
                onClick={() => changeAssignmentMode('AUTO')}
              >
                {t('set.autoAssign.open')}
              </button>
            ) : null}
          </div>
          {assignmentMode === 'MANUAL' && gateQ.data ? (
            <p
              data-testid="auto-assign-gate"
              style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}
            >
              {gateSummary(gateQ.data)}
            </p>
          ) : null}
          {assignmentMode === 'MANUAL' &&
          gateQ.data &&
          !gateQ.data.eligible &&
          gateQ.data.reasonKey ? (
            <p
              data-testid="auto-assign-reason"
              style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}
            >
              {t(gateQ.data.reasonKey, {
                rate: gateQ.data.minRatePct,
                days: gateQ.data.minDays,
                samples: gateQ.data.minSamples,
              })}
            </p>
          ) : null}
          {gateQ.isError ? (
            <p role="alert" style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
              {t('set.autoAssign.loadError')}
            </p>
          ) : null}
          {assignmentMode === 'MANUAL' && !isOwner ? (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
              {t('set.autoAssign.ownerOnly')}
            </p>
          ) : null}
          {updateAssignmentMode.isError ? <ErrorLine error={updateAssignmentMode.error} /> : null}
        </section>
      </SettingsGroup>

      <SettingsGroup id="hours" title={t('set.group.hours')} hint={t('set.group.hoursHint')}>
        {/* ── Bildirim sessiz saati (N1). Değiştirmek yalnız OWNER; MANAGER değeri okur. ── */}
        <section
          className="card"
          style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <strong style={{ fontSize: 15 }}>{t('set.quietHours')}</strong>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
            {t('set.quietHoursHint')}
          </p>
          {isOwner ? (
            <div
              className="form-grid"
              style={
                {
                  '--form-cols': '1fr 1fr',
                  '--form-gap': '10px',
                  alignItems: 'end',
                } as React.CSSProperties
              }
            >
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="section-label">{t('set.quietStart')}</span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {t('set.quietStartHint')}
                </span>
                <input
                  type="time"
                  className="input"
                  style={{ height: 44 }}
                  data-testid="quiet-start"
                  value={quietDraft?.start ?? ''}
                  onChange={(e) =>
                    setQuietDraft({ ...(quietDraft ?? serverQuiet), start: e.target.value })
                  }
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="section-label">{t('set.quietEnd')}</span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {t('set.quietEndHint')}
                </span>
                <input
                  type="time"
                  className="input"
                  style={{ height: 44 }}
                  data-testid="quiet-end"
                  value={quietDraft?.end ?? ''}
                  onChange={(e) =>
                    setQuietDraft({ ...(quietDraft ?? serverQuiet), end: e.target.value })
                  }
                />
              </label>
            </div>
          ) : (
            <p
              data-testid="quiet-owner-only"
              style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}
            >
              {serverQuiet.start} – {serverQuiet.end} · {t('set.quietOwnerOnly')}
            </p>
          )}
          {isOwner ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-primary"
                data-testid="quiet-save"
                disabled={!quietValid || !quietDirty || updateSettings.isPending}
                onClick={saveQuietHours}
              >
                {t('common.save')}
              </button>
            </div>
          ) : null}
        </section>
      </SettingsGroup>

      <SettingsGroup
        id="notifications"
        title={t('set.group.notifications')}
        hint={t('set.group.notificationsHint')}
      >
        {/* ── Test bildirimi: bildirim hattı çalışıyor mu diye elle denenir. ── */}
        {/* Kendi kartı: sessiz saat kartının içinde olursa yönetici "sessiz saat başlığı altında */}
        {/* bildirim düğmesi" görür ve neyin ne olduğu anlaşılmaz. */}
        <section
          className="card"
          style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <strong style={{ fontSize: 15 }}>{t('panel.settings.testNotification.title')}</strong>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              className="btn"
              data-testid="test-notification"
              disabled={sendTest.isPending}
              onClick={sendTestNotification}
            >
              {t('panel.settings.testNotification.button')}
            </button>
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
              {t('panel.settings.testNotification.hint')}
            </p>
          </div>
        </section>
      </SettingsGroup>

      <details data-testid="settings-advanced" className="card" style={{ padding: '12px 20px' }}>
        <summary style={{ cursor: 'pointer', fontSize: 15, fontWeight: 700 }}>
          {t('set.group.advanced')}
        </summary>
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '8px 0 14px' }}>
          {t('set.group.advancedHint')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* ── Hayat'a yazma kipi (paket H). YALNIZ OWNER görür ve değiştirir.
           MANAGER ekranı açarsa "Bu ayar yalnız patron tarafından değiştirilir" rozeti görür
           ve kutu kapalı kalır; kural: kip değişikliği tek kişinin inisiyatifidir. ── */}
          <section
            className="card"
            style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong style={{ fontSize: 15 }}>{t('hayatPush.setting.title')}</strong>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
              {t('hayatPush.setting.intro')}
            </p>

            {!isOwner ? (
              <p
                data-testid="hayat-mode-owner-only"
                style={{
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                  background: 'var(--page-bg)',
                  borderRadius: 'var(--r-box)',
                  padding: 12,
                  margin: 0,
                }}
              >
                {t('hayatWriteMode.notFromSettings')}
              </p>
            ) : (
              <div
                className="form-grid"
                style={
                  {
                    '--form-cols': '1fr 1fr',
                    '--form-gap': '10px',
                    alignItems: 'end',
                  } as React.CSSProperties
                }
              >
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span className="section-label">{t('hayatPush.setting.modeLabel')}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('set.hayatModeHint')}
                  </span>
                  <select
                    className="input"
                    style={{ height: 44 }}
                    value={modeDraft ?? ''}
                    data-testid="hayat-mode-select"
                    onChange={(e) => {
                      const next = e.target.value as HayatWriteMode;
                      setModeDraft(next);
                      // Otomatik seçimde uyarı metnini AÇIK tutmak için confirmDraft'ı da eşitle —
                      // kullanıcı farkında olmadan "Onaylı" seçip onayı boş bırakmasın.
                      if (confirmDraft === null) setConfirmDraft(next);
                    }}
                  >
                    {HAYAT_WRITE_MODES.map((m) => (
                      <option key={m} value={m}>
                        {t(`hayatWriteMode.${m}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span className="section-label">{t('hayatPush.setting.confirmLabel')}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('set.hayatConfirmHint')}
                  </span>
                  <select
                    className="input"
                    style={{ height: 44 }}
                    value={confirmDraft ?? ''}
                    data-testid="hayat-mode-confirm"
                    onChange={(e) => setConfirmDraft(e.target.value as HayatWriteMode)}
                  >
                    {HAYAT_WRITE_MODES.map((m) => (
                      <option key={m} value={m}>
                        {t(`hayatWriteMode.${m}`)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            {isOwner && modeDraft === 'AUTO' ? (
              <p
                role="alert"
                data-testid="hayat-mode-auto-warning"
                style={{
                  fontSize: 12.5,
                  color: 'var(--warning)',
                  background: 'var(--page-bg)',
                  borderRadius: 'var(--r-box)',
                  padding: 10,
                  margin: 0,
                }}
              >
                {t('hayatPush.setting.autoWarning')}
              </p>
            ) : null}

            {isOwner &&
            modeDraft !== null &&
            confirmDraft !== null &&
            modeDraft !== confirmDraft ? (
              <p
                role="alert"
                data-testid="hayat-mode-mismatch"
                style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}
              >
                {t('hayatPush.setting.mismatch')}
              </p>
            ) : null}

            {updateHayatWriteMode.isError ? <ErrorLine error={updateHayatWriteMode.error} /> : null}

            {isOwner ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-primary"
                  disabled={!hayatModeValid || updateHayatWriteMode.isPending}
                  onClick={submitHayatMode}
                  data-testid="hayat-mode-save"
                >
                  {t('common.save')}
                </button>
              </div>
            ) : null}
          </section>

          {/* ── Eşikler ── */}
          <section
            className="card"
            style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <strong style={{ fontSize: 15 }}>{t('set.thresholds')}</strong>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
              {t('set.thresholdsHint')}
            </p>
            {updateSettings.isError ? <ErrorLine error={updateSettings.error} /> : null}

            {THRESHOLDS.map((f) => {
              const raw = draft[f.key] ?? '';
              const n = Number(raw);
              const invalid = raw.trim() !== '' && (!Number.isInteger(n) || n < f.min || n > f.max);
              return (
                <label
                  key={f.key}
                  className="form-grid"
                  style={
                    {
                      '--form-cols': '1fr 110px 90px',
                      alignItems: 'center',
                      minHeight: 62,
                      borderTop: '1px solid var(--border)',
                      paddingTop: 12,
                    } as React.CSSProperties
                  }
                >
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{t(f.labelKey)}</span>
                    <span
                      style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}
                    >
                      {t(f.hintKey)}
                    </span>
                  </span>
                  <input
                    value={raw}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    inputMode="numeric"
                    style={{
                      height: 44,
                      fontVariantNumeric: 'tabular-nums',
                      borderColor: invalid ? 'var(--danger)' : undefined,
                    }}
                  />
                  <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    {invalid ? t('set.range', { min: f.min, max: f.max }) : t(f.unitKey)}
                  </span>
                </label>
              );
            })}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-primary"
                disabled={!dirty || updateSettings.isPending}
                onClick={save}
              >
                {t('common.save')}
              </button>
            </div>
          </section>
        </div>
      </details>
    </div>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div
      className="form-grid"
      style={
        {
          '--form-cols': '200px 1fr',
          minHeight: 40,
          alignItems: 'center',
          borderTop: '1px solid var(--border)',
        } as React.CSSProperties
      }
    >
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>{value}</span>
        {hint ? <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{hint}</span> : null}
      </span>
    </div>
  );
}

/** Ayar grubu: başlık, tek cümle açıklama ve kartlar. Acil durum (kill switch) grubu DEĞİLDİR; o üstte kalır. */
function SettingsGroup({
  id,
  title,
  hint,
  children,
}: {
  id: string;
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section
      data-testid={`settings-group-${id}`}
      aria-label={title}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div>
        <h2 className="h2" style={{ margin: 0 }}>
          {title}
        </h2>
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '2px 0 0' }}>{hint}</p>
      </div>
      {children}
    </section>
  );
}

/** Yalnız durum gösterir; yazma kontrolü başka ekranda (Entegrasyonlar). */
function FlagStatus({ label, on }: { label: string; on: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 44,
        borderTop: '1px solid var(--border)',
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 13.5, flex: 1 }}>{label}</span>
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: on ? 'var(--success)' : 'var(--text-secondary)',
        }}
      >
        {on ? t('set.on') : t('set.off')}
      </span>
    </div>
  );
}

/**
 * Açma/kapama satırı. KAPATMA (Durdur) onay ister — yıkıcı işlem; AÇMA onaysızdır.
 */
function FlagRow({
  label,
  on,
  pending,
  onToggle,
  offBody = t('set.flagOffBody'),
}: {
  label: string;
  on: boolean;
  pending: boolean;
  onToggle: (enabled: boolean) => void;
  /** Kapatma onayının metni. Açma yetkisi kısıtlı bayraklarda (yazma) farklı yazılır. */
  offBody?: string;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 52,
        borderTop: '1px solid var(--border)',
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 13.5, flex: 1 }}>{label}</span>
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: on ? 'var(--success)' : 'var(--text-secondary)',
        }}
      >
        {on ? t('set.on') : t('set.off')}
      </span>
      <button
        className={on ? 'btn' : 'btn btn-primary'}
        disabled={pending}
        onClick={() => (on ? setConfirming(true) : onToggle(true))}
      >
        {on ? t('set.turnOff') : t('set.turnOn')}
      </button>
      {confirming ? (
        <ConfirmDialog
          title={t('set.flagOffTitle', { label })}
          body={offBody}
          confirmLabel={t('set.turnOff')}
          tone="danger"
          pending={pending}
          onConfirm={() => {
            setConfirming(false);
            onToggle(false);
          }}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const message = (error as { message?: string } | null)?.message;
  return (
    <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
      {message && message.length > 0 ? message : t('error.internal')}
    </p>
  );
}
